import consumer from "../channels/consumer"
import { estimateTimeRemaining, validateField, debounce } from './utils/import_utils'

export function initProductImport() {
  const importContainer = document.querySelector('[data-import-container]')
  if (!importContainer) return

  const importId = importContainer.dataset.importId
  window.importStartTime = Date.now()
  console.log('Initializing channel for import', importId)

  // Initialize tooltips
  initializeTooltips()

  // Initialize field validation
  initializeFieldValidation()

  consumer.subscriptions.create(
    { channel: "ProductImportChannel", import_id: importId },
    {
      connected() {
        console.log("Connected to product import channel")
        updateStatus('Connected to import channel')
      },

      disconnected() {
        console.log("Disconnected from product import channel")
        updateStatus('Disconnected from import channel')
      },

      received(data) {
        // Log the raw incoming data FIRST
        console.log("[ProductImportChannel] Received raw data:", JSON.stringify(data));

        // Existing console log (keep for comparison)
        console.log("Received data:", data);

        // Check for mapping_url first and redirect if present
        if (data.mapping_url) {
          console.log(`Redirecting to mapping page: ${data.mapping_url}`);
          setTimeout(() => {
             window.location.href = data.mapping_url;
          }, 500);
          return;
        }

        // Check for validated_redirect_url and redirect/reload if present
        if (data.validated_redirect_url) {
          // Compare with the current URL (ignoring potential query strings/hashes for robustness if needed,
          // but direct comparison is often sufficient if URLs are consistent)
          const currentUrl = window.location.href;
          const targetUrl = data.validated_redirect_url;

          if (currentUrl === targetUrl) {
            console.log(`Already on show page. Reloading page: ${currentUrl}`);
            // Use a short delay before reloading
            setTimeout(() => {
              window.location.reload();
            }, 1000); // 1 second delay
          } else {
            console.log(`Redirecting to show page after validation: ${targetUrl}`);
            // Allow slightly longer delay to read 'Validation completed' message
            setTimeout(() => {
               window.location.href = targetUrl;
            }, 1000); // 1 second delay
          }
          return;
        }

        // Update status if message is present (even if redirecting soon)
        if (data.message) {
          updateStatus(data.message);
        }

        // Update progress if present
        if (data.progress) {
          updateProgress(data.progress);
        }

        // Handle final completion/failure redirection (only if not already redirected)
        if (data.redirect_url) {
          handleComplete(data);
        }
      }
    }
  )
}

function updateProgress(payload) {
  const { current, total, percentage } = payload
  const progressBar = document.querySelector('[data-progress-bar]')
  const progressText = document.querySelector('[data-progress-text]')
  const timeRemaining = document.querySelector('[data-time-remaining]')

  if (progressBar && progressText) {
    // Update progress bar
    progressBar.style.width = `${percentage}%`
    progressBar.setAttribute('aria-valuenow', percentage)

    // Update progress text with enhanced formatting
    progressText.textContent = `${current.toLocaleString()} / ${total.toLocaleString()} (${percentage}%)`

    // Update time remaining
    if (timeRemaining && window.importStartTime) {
      const estimate = estimateTimeRemaining(current, total, window.importStartTime)
      timeRemaining.textContent = `Estimated time remaining: ${estimate.formatted}`
    }
  }
}

function updateStatus(message) {
  const statusText = document.querySelector('[data-status-text]')
  const statusContainer = document.querySelector('[data-status-container]')

  if (statusText) {
    statusText.textContent = message

    // Add visual feedback
    statusContainer?.classList.remove('fade-out')
    statusContainer?.classList.add('fade-in')

    // Fade out after 5 seconds if not an error message
    if (message && !message.toLowerCase().includes('error')) {
      setTimeout(() => {
        statusContainer?.classList.remove('fade-in')
        statusContainer?.classList.add('fade-out')
      }, 5000)
    }
  }
}

function showValidationError(payload) {
  const { field, message } = payload
  const fieldElement = document.querySelector(`[data-field="${field}"]`)

  if (fieldElement) {
    // Add error class
    fieldElement.classList.add('error')

    // Show error message
    const errorElement = document.createElement('div')
    errorElement.className = 'text-red-600 text-sm mt-1'
    errorElement.textContent = message

    // Replace existing error message if any
    const existingError = fieldElement.parentNode.querySelector('.text-red-600')
    if (existingError) {
      existingError.remove()
    }

    fieldElement.parentNode.appendChild(errorElement)
  }
}

function handleComplete(payload) {
  const { redirect_url, status, message } = payload

  // Show completion message
  updateStatus(message)

  // Add success/error styling
  const container = document.querySelector('[data-import-container]')
  if (container) {
    container.classList.add(status === 'success' ? 'border-green-500' : 'border-red-500')
  }

  // setTimeout(() => {
  //   // reload the page
  //   window.location.reload()
  // }, 1500)

  // Redirect after a short delay if URL provided
  if (redirect_url) {
    setTimeout(() => {
      window.location.href = redirect_url
    }, 1500)
  }
}

function initializeTooltips() {
  const tooltips = document.querySelectorAll('[data-tooltip]')
  tooltips.forEach(tooltip => {
    const content = tooltip.dataset.tooltip

    tooltip.addEventListener('mouseenter', () => {
      const tooltipElement = document.createElement('div')
      tooltipElement.className = 'absolute z-50 p-2 bg-gray-900 text-white text-sm rounded shadow-lg max-w-xs'
      tooltipElement.textContent = content
      tooltip.appendChild(tooltipElement)
    })

    tooltip.addEventListener('mouseleave', () => {
      const tooltipElement = tooltip.querySelector('.absolute')
      tooltipElement?.remove()
    })
  })
}

function initializeFieldValidation() {
  const fields = document.querySelectorAll('[data-validate]')
  fields.forEach(field => {
    const validateOnChange = debounce(async (event) => {
      const result = await validateField(field.name, event.target.value)

      // Remove existing validation classes
      field.classList.remove('border-red-500', 'border-green-500')

      // Add appropriate validation class
      field.classList.add(result.valid ? 'border-green-500' : 'border-red-500')

      // Show/hide error message
      const errorContainer = field.parentNode.querySelector('.text-red-600')
      if (result.error && !errorContainer) {
        showValidationError({ field: field.name, message: result.error })
      } else if (!result.error && errorContainer) {
        errorContainer.remove()
      }
    }, 300)

    field.addEventListener('input', validateOnChange)
  })
}
